import { Button, Card, CardContent, Form, Tooltip } from '@packages/ui';
import { UseFormReturn } from 'react-hook-form';
import { Check, Edit } from 'lucide-react';
import { cn } from '@packages/utils';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

type Props<T extends Record<string, unknown>> = {
  question: string;
  form: UseFormReturn<T>;
  isDone: boolean;
  isActive: boolean;
  onActivate: () => void;
  onNext: (data: T) => void;
  autofocusField?: keyof T;
  children: React.ReactNode;
  hideSubmit?: boolean;
  ctaText?: string;
};

export const Step = <T extends Record<string, unknown>>({
  question,
  form,
  isDone,
  isActive,
  onActivate,
  onNext,
  children,
  autofocusField,
  hideSubmit = false,
  ctaText,
}: Props<T>) => {
  useEffect(() => {
    if (isActive) {
      const fields = Object.keys(form.getValues());
      if (fields.length > 0 || autofocusField) {
        form.setFocus(autofocusField ?? (fields[0] as any));
      }
    }
  }, [isActive, form, autofocusField]);

  const { t } = useTranslation();

  const buttonText = useMemo(() => ctaText ?? t('next'), [ctaText, t]);

  return (
    <Form {...form}>
      <form onSubmit={(event) => void form.handleSubmit(onNext)(event)} autoFocus>
        <Card
          className={cn(
            'overflow-hidden relative transition-all duration-1000 ease-in-out',
            isActive ? 'max-h-[2000px]' : 'max-h-16',
            !isDone && !isActive && 'opacity-50',
          )}>
          <CardContent className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h2
                className={cn('text-lg font-semibold grow', isDone && 'cursor-pointer')}
                onClick={() => isDone && onActivate()}>
                {question}
              </h2>
              <div className={cn('flex gap-2 items-center', isDone ? 'opacity-100' : 'opacity-0')}>
                <Check className={cn('text-primary transition-all duration-1000 ease-in-out')} />
                <Tooltip content={t('editAnswer')} delayDuration={0}>
                  <button
                    type="button"
                    className="border p-1 rounded text-foreground border-primary"
                    onClick={() => onActivate()}>
                    <Edit size={16} />
                  </button>
                </Tooltip>
              </div>
            </div>
            <div
              className={`transition-all duration-1000 ease-in-out ${
                isActive ? 'opacity-100' : 'opacity-0'
              }`}>
              {children}
              {isActive && !hideSubmit ? (
                <>
                  <Button
                    type="submit"
                    variant="default"
                    className="w-full mt-4"
                    disabled={!isActive}>
                    {buttonText}
                  </Button>
                </>
              ) : null}
            </div>
          </CardContent>
        </Card>
      </form>
    </Form>
  );
};
